// src/ui/components/EventModal.invite.js
// Envío de invitación de calendario (.ics) a los miembros asignados a un evento (lineup).
// Comunicación INTERNA (músicos/técnicos), no a contactos/clientes.
// Clonado del patrón de EventModal.emails.js.

import { actions } from "../../app/state.js";
import { getEventLineupWithEmails } from "../../services/lineupService.js";
import { sendInternalEmail } from "../../services/emailsService.js";
import { getCurrentUserAndProfile } from "../../services/teamService.js";
import { canSendInternalEmail } from "../../services/rbac.js";
import { buildEventIcs, icsToBase64 } from "../../utils/icsUtils.js";
import { $, escapeHtml } from "./EventModal.utils.js";

/**
 * Engancha el botón "Enviar invite a la banda" dentro de la sección de lineup.
 * Solo visible para admin/manager (canSendInternalEmail).
 */
export async function wireInviteLogic(root, eventId, eventData) {
  const btn = $(root, "#event-lineup-invite");
  if (!btn || !eventId) return;

  // Gate por permisos: solo admin/manager
  const allowed = await canSendInternalEmail();
  if (!allowed) {
    btn.classList.add("hidden");
    return;
  }
  btn.classList.remove("hidden");
  btn.classList.add("flex");

  btn.onclick = () => openInviteModal({ eventId, eventData });
}

async function openInviteModal({ eventId, eventData }) {
  // Resolver asignados + emails
  let lineup = [];
  try {
    lineup = await getEventLineupWithEmails(eventId);
  } catch (e) {
    console.error(e);
    actions.addToast?.({ type: "error", message: "No se pudo cargar el lineup." });
    return;
  }

  if (lineup.length === 0) {
    actions.addToast?.({ type: "warning", message: "No hay miembros asignados a este evento." });
    return;
  }

  const withEmail = lineup.filter((m) => m.email);
  const withoutEmail = lineup.filter((m) => !m.email);

  if (withEmail.length === 0) {
    actions.addToast?.({
      type: "warning",
      message: "Ningún miembro asignado tiene email. Edítalos en Gestión de equipo.",
    });
    return;
  }

  const dateLabel = eventData?.date
    ? new Date(`${eventData.date}T00:00:00`).toLocaleDateString("es-ES", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      })
    : "(sin fecha)";
  const eventName = eventData?.client_name_legacy || "Evento";
  const location = eventData?.location || eventData?.city || "";

  const modalHtml = `
    <div class="modal-backdrop" id="invite-backdrop">
      <div class="modal-dialog">
        <div class="modal-header flex items-center justify-between">
          <h3 class="font-bold text-slate-800 flex items-center gap-2">
            <i class="fa-solid fa-calendar-check text-blue-500"></i>
            Enviar invite a la banda
          </h3>
          <button id="invite-close" class="text-slate-400 hover:text-slate-600 p-2"><i class="fa-solid fa-xmark"></i></button>
        </div>

        <div class="modal-body space-y-4">
          <div class="p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600">
            <div class="font-bold text-slate-800">${escapeHtml(eventName)}</div>
            <div class="text-xs mt-0.5"><i class="fa-solid fa-calendar-day mr-1 text-slate-400"></i>${escapeHtml(dateLabel)}</div>
            ${location ? `<div class="text-xs mt-0.5"><i class="fa-solid fa-location-dot mr-1 text-slate-400"></i>${escapeHtml(location)}</div>` : ""}
          </div>

          <div>
            <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Recibirán la invitación (${withEmail.length})</label>
            <div class="flex flex-wrap gap-2">
              ${withEmail.map((m) => `
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-100 rounded-lg text-[11px] font-medium text-blue-700">
                  <i class="fa-solid fa-user text-[9px]"></i>${escapeHtml(m.member_name)}
                </span>`).join("")}
            </div>
          </div>

          ${withoutEmail.length ? `
          <div class="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <p class="text-[11px] text-amber-700 flex items-start gap-1.5">
              <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
              <span>Sin email (se omiten): <strong>${withoutEmail.map((m) => escapeHtml(m.member_name)).join(", ")}</strong>. Añade su email en Gestión de equipo.</span>
            </p>
          </div>` : ""}

          <div>
            <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Asunto</label>
            <input type="text" id="invite-subject" class="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" value="${escapeHtml(`Bolo: ${eventName} — ${dateLabel}`)}">
          </div>

          <div>
            <label class="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Mensaje</label>
            <textarea id="invite-body" rows="5" class="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none">Hola,

Os paso la invitación de calendario para el bolo de ${eventName} el ${dateLabel}${location ? ` en ${location}` : ""}.

Añadidla a vuestro calendario. ¡Gracias!</textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button id="invite-cancel" class="px-5 py-2.5 text-slate-600 font-medium hover:bg-slate-200/50 rounded-xl transition-colors">Cancelar</button>
          <button id="invite-send" class="px-6 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 shadow-lg shadow-slate-900/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <span>Enviar invite</span><i class="fa-solid fa-paper-plane text-xs"></i>
          </button>
        </div>
      </div>
    </div>`;

  const el = document.createElement("div");
  el.innerHTML = modalHtml;
  document.body.appendChild(el);

  const close = () => el.remove();
  document.getElementById("invite-close").onclick = close;
  document.getElementById("invite-cancel").onclick = close;

  const sendBtn = document.getElementById("invite-send");
  sendBtn.onclick = async () => {
    const subject = document.getElementById("invite-subject").value.trim();
    const body = document.getElementById("invite-body").value.trim();
    if (!subject || !body) {
      actions.addToast?.({ type: "warning", message: "Asunto y mensaje son obligatorios." });
      return;
    }

    sendBtn.disabled = true;
    sendBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin mr-2"></i>Enviando...';

    try {
      // Emisor (ORGANIZER) = usuario actual
      const { user } = await getCurrentUserAndProfile();
      const organizerEmail = user?.email || "hola@wearelostoldos.com";

      // Construir .ics
      const ics = buildEventIcs({
        eventId,
        summary: eventName,
        description: body,
        location,
        date: eventData?.date,
        organizerEmail,
        attendees: withEmail.map((m) => ({ email: m.email, name: m.member_name })),
      });

      const icsAttachment = {
        filename: "invite.ics",
        content: icsToBase64(ics),
        content_type: "text/calendar; method=REQUEST; charset=utf-8",
      };

      await sendInternalEmail({
        toEmails: withEmail.map((m) => m.email),
        recipientMemberIds: withEmail.map((m) => m.member_id),
        subject,
        body,
        eventId,
        icsAttachment,
      });

      actions.addToast?.({ type: "success", message: `Invite enviada a ${withEmail.length} miembro(s).` });
      close();
    } catch (err) {
      console.error(err);
      actions.addToast?.({ type: "error", message: "Error al enviar la invitación." });
      sendBtn.disabled = false;
      sendBtn.innerHTML = '<span>Enviar invite</span><i class="fa-solid fa-paper-plane text-xs"></i>';
    }
  };
}
